// NoteNode.js - Sticky Note Component for Canvas Annotations

import React, { memo } from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';

const NoteNode = ({ data, id }) => {
  // Get note parameters
  const text = data.parameters?.text?.value || 'Add your notes here...';
  const color = data.parameters?.color?.value || 'yellow';

  // Map color names to theme colors
  const colorMap = {
    yellow: THEME.annotationYellow,
    orange: THEME.annotationOrange,
    red: THEME.annotationRed,
    cyan: THEME.annotationCyan
  };

  const backgroundColor = colorMap[color] || THEME.annotationYellow;

  return (
    <NoteContainer style={{ backgroundColor }}>
      <NoteText>{text}</NoteText>
      <NoteCorner style={{ backgroundColor }} />
    </NoteContainer>
  );
};

// Styled Components
const NoteContainer = styled.div`
  position: relative;
  min-width: 180px;
  max-width: 280px;
  min-height: 150px;
  padding: 16px;
  padding-top: 12px;
  border-radius: 2px;
  box-shadow:
    2px 2px 8px rgba(0, 0, 0, 0.2),
    inset 0 -1px 2px rgba(0, 0, 0, 0.1);
  transform: rotate(-1deg);
  cursor: move;
  transition: transform ${THEME.transitionFast}, box-shadow ${THEME.transitionFast};
  display: flex;
  align-items: center;
  justify-content: center;

  /* Add subtle texture */
  background-image:
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 19px,
      rgba(0, 0, 0, 0.02) 19px,
      rgba(0, 0, 0, 0.02) 20px
    );

  /* Hover effect */
  &:hover {
    transform: rotate(-0.5deg) scale(1.01);
    box-shadow:
      3px 3px 12px rgba(0, 0, 0, 0.25),
      inset 0 -1px 2px rgba(0, 0, 0, 0.1);
  }

  /* Selected state */
  .react-flow__node.selected & {
    outline: 2px solid ${THEME.accentPrimary};
    outline-offset: 2px;
    transform: rotate(0deg) scale(1.02);
  }

  /* Double-click hint */
  &::before {
    content: 'Double-click to edit';
    position: absolute;
    bottom: 4px;
    right: 8px;
    font-size: ${THEME.fontSize.xs};
    color: rgba(0, 0, 0, 0.3);
    opacity: 0;
    transition: opacity ${THEME.transitionFast};
  }

  &:hover::before {
    opacity: 1;
  }
`;

const NoteText = styled.div`
  color: #333;
  font-size: ${THEME.fontSize.md};
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  text-align: center;
  width: 100%;

  /* Empty state styling */
  &:empty::before {
    content: 'Add your notes here...';
    color: rgba(0, 0, 0, 0.4);
  }
`;

// Folded corner effect
const NoteCorner = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 20px 20px 0;
  border-color: transparent rgba(0, 0, 0, 0.1) transparent transparent;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -20px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 20px 20px 0;
    border-color: transparent #fff transparent transparent;
    filter: brightness(0.95);
  }
`;

export default memo(NoteNode);
